import { useState } from "react";
import {
  Eye,
  EyeOff,
  User,
  KeyRound,
  Save,
  X,
  Edit,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { updateUserPassword, updateUserProfile } from "../api/userServices";
import { updateUser, logout } from "../store/userSlice";
import { toast } from "react-toastify";
import { profileButtonSpinner } from "../assets/icons";
import Avatar from "react-avatar-edit";
import { uploadUserProfileImage, deleteCompany } from "../api/userServices";
import { useNavigate } from "react-router-dom";

export default function UserSettings() {
  const { user } = useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState("profile");

  const { userProfileImageUrl } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const initialProfile = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    designation: user?.designation || "",
  };
  // Loading states
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  // Profile state
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(initialProfile);

  // Profile picture state
  const [previewUrl, setPreviewUrl] = useState(user?.profilePicture || null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  // Delete organisation (super admin only)
  const navigate = useNavigate();
  const companyName = user?.companyData?.companyName || "";
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ companyName: "", password: "" });
  const [isDeleting, setIsDeleting] = useState(false);

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteForm({ companyName: "", password: "" });
  };

  const handleDeleteCompany = async () => {
    try {
      setIsDeleting(true);
      await deleteCompany(deleteForm);
      toast.success("Your organisation has been deleted.");
      dispatch(logout());
      localStorage.removeItem("token");
      navigate("/portal/login", { replace: true });
    } catch (error) {
      toast.error(error?.error || "Failed to delete organisation. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  // Password state
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleProfileUpdate = async () => {
    try {
      setIsProfileLoading(true);
      const response = await updateUserProfile(profile);
      dispatch(updateUser(response.user));
      setIsEditing(false);
      toast.dismiss();
      toast.success(response.message || "Profile updated successfully!", {
        autoClose: 3000,
      });
      setIsProfileLoading(false);
    } catch (error) {
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleChangePassword = async () => {
    const { current, new: newPassword, confirm } = passwords;

    toast.dismiss();
    if (!current) {
      toast.error("Current password is required.");
      return;
    }
    if (!newPassword) {
      toast.error("New password is required.");
      return;
    }
    if (!confirm) {
      toast.error("Please confirm your new password.");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    try {
      setIsPasswordLoading(true);
      const response = await updateUserPassword({
        currentPassword: current,
        newPassword,
      });

      if (response.status) {
        toast.success("Password updated successfully!", { autoClose: 3000 });
        setPasswords({ current: "", new: "", confirm: "" });
        localStorage.removeItem("token");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          "Failed to update password. Please try again.",
      );
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const updateProfilePicture = async () => {
    try {
      setIsUploadingPicture(true);

      const response = await uploadUserProfileImage(previewUrl);
      console.log("🚀 ~ :121 ~ updateProfilePicture ~ response:", response);
      dispatch(updateUser(response.user));

      toast.success("Profile picture updated successfully!", {
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to update profile picture. Please try again.");
    } finally {
      setIsUploadingPicture(false);
    }
  };

  return (
    <div className="w-full h-full mx-auto px-4 py-8 font-raleway overflow-hidden">
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab("profile")}
          className={`flex items-center gap-2 px-5 py-3 border rounded ${
            activeTab === "profile"
              ? "bg-lightBlue text-white"
              : "bg-white border-gray-300 text-gray-700  hover:bg-darkBlue hover:text-white transition-colors duration-300"
          }`}
          disabled={isProfileLoading || isPasswordLoading}
        >
          <User size={20} />
          Profile Edit
        </button>
        <button
          onClick={() => setActiveTab("password")}
          className={`flex items-center gap-2 px-5 py-3 border rounded ${
            activeTab === "password"
              ? "bg-lightBlue text-white"
              : "bg-white border-gray-300 text-gray-700 hover:bg-darkBlue hover:text-white transition-colors duration-300 "
          } `}
          disabled={isProfileLoading || isPasswordLoading}
        >
          <KeyRound size={20} />
          Change Password
        </button>
      </div>

      {/* Content */}
      {activeTab === "profile" ? (
        <div className="space-y-8 overflow-auto pb-20 w-full h-full ">
          {/* Profile Picture Upload Section */}
          <div className="border border-gray-300 rounded-lg bg-white">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-300 bg-gray-300">
              <h3 className="text-xl font-semibold text-black">
                Profile Picture
              </h3>
            </div>

            <div className="">
              <div className="p-6 flex flex-col md:flex-row items-center md:gap-8">
                <div>
                  <Avatar
                    width={250}
                    height={250}
                    onBeforeFileLoad={() => {}}
                    onCrop={(preview) => setPreviewUrl(preview)}
                    onClose={() => setPreviewUrl(null)}
                    label="Choose picture"
                    shadingColor="#ffffff"
                    backgroundColor="black"
                    borderColor="#ccc"
                  />
                </div>
                <div className="flex flex-col items-center gap-4">
                  <img
                    src={previewUrl || userProfileImageUrl}
                    alt=""
                    className="w-40 h-40 rounded-full object-cover border"
                  />

                  <button
                    onClick={() => {
                      updateProfilePicture();
                    }}
                    className={`flex items-center gap-2 border border-gray-400 px-6 py-2 rounded  hover:bg-darkBlue disabled:cursor-not-allowed hover:text-white ${
                      isUploadingPicture
                        ? "bg-lightBlue text-white"
                        : "bg-white text-gray-700"
                    } ${isUploadingPicture ? "opacity-60 cursor-not-allowed" : ""}`}
                    disabled={isUploadingPicture}
                  >
                    {isUploadingPicture
                      ? profileButtonSpinner
                      : "Update Picture"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Edit Profile Section */}
          <div className="border border-gray-300 rounded-lg bg-white ">
            <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-300">
              <h3 className="text-xl font-semibold text-black">Edit Profile</h3>
              <div className="flex items-center space-x-4">
                <div
                  className={`transition-all duration-500 ${
                    isEditing
                      ? "opacity-100 scale-100 translate-y-0"
                      : "opacity-0 "
                  }`}
                >
                  <button
                    onClick={() => {
                      setIsEditing((prev) => !prev);
                      setProfile(initialProfile);
                    }}
                    className={`flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded `}
                    disabled={isProfileLoading}
                  >
                    <X size={18} />
                    Cancel
                  </button>
                </div>
                <button
                  onClick={() => {
                    setIsEditing((prev) => !prev);
                    if (isEditing) {
                      handleProfileUpdate();
                    }
                  }}
                  className={`flex items-center gap-2 border border-gray-400 px-6 py-2 rounded transition-all duration-300  hover:bg-darkBlue disabled:cursor-not-allowed hover:text-white ${
                    isEditing || isProfileLoading
                      ? "bg-lightBlue text-white"
                      : "bg-white text-gray-700"
                  } ${isProfileLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                  disabled={
                    isProfileLoading ||
                    (isEditing &&
                      JSON.stringify(profile) ===
                        JSON.stringify(initialProfile))
                  }
                >
                  {isProfileLoading ? (
                    profileButtonSpinner
                  ) : isEditing ? (
                    <Save size={18} />
                  ) : (
                    <Edit size={18} />
                  )}

                  {isProfileLoading ? "Saving..." : isEditing ? "Save" : "Edit"}
                </button>
              </div>
            </div>

            {/* Grid Inputs */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {["firstName", "lastName", "email", "designation"].map(
                  (field) => (
                    <div key={field}>
                      <label className="block  font-medium mb-1 capitalize">
                        {field.replace(/([A-Z])/g, " $1")}
                      </label>
                      <input
                        type="text"
                        name={field}
                        placeholder={`Enter ${field}`}
                        value={profile[field]}
                        onChange={handleProfileChange}
                        disabled={
                          !isEditing ||
                          isProfileLoading ||
                          (field === "email" && isEditing)
                        }
                        className="w-full border border-gray-300 rounded px-3 py-3 disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-lightBlue transition-colors"
                      />
                    </div>
                  ),
                )}

                {/* Phone with +92 */}
                <div>
                  <label className="block  font-medium mb-1">
                    Phone Number
                  </label>
                  <div className="flex">
                    <input
                      type="tel"
                      name="phoneNumber"
                      placeholder="Phone Number"
                      value={profile.phoneNumber}
                      onChange={handleProfileChange}
                      disabled={!isEditing || isProfileLoading}
                      className="w-full border border-gray-300 rounded px-3 py-3 disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-lightBlue transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Danger Zone (super admin only) */}
          {user?.role === "super_admin" && (
            <div className="border border-red-300 rounded-lg bg-white">
              <div className="px-6 py-4 border-b border-red-300 bg-red-50">
                <h3 className="text-xl font-semibold text-red-700">Danger Zone</h3>
              </div>
              <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="font-medium">Delete this organisation</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Permanently deletes <strong>{companyName}</strong>, all its
                    users, projects and workflows. This cannot be undone.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded whitespace-nowrap transition-colors"
                >
                  <Trash2 size={18} />
                  Delete Organisation
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="mt-8 border-y py-4 mb-6">
            <h2 className="text-2xl font-semibold py-[5px]">Change Password</h2>
          </div>
          <div className="space-y-6 max-w-md">
            {["current", "new", "confirm"].map((field) => (
              <div key={field}>
                <label className="block font-medium mb-1 capitalize">
                  {field === "confirm"
                    ? "Confirm Password"
                    : `${field} Password`}
                </label>
                <div className="relative">
                  <input
                    type={show[field] ? "text" : "password"}
                    name={field}
                    placeholder={`Enter ${field} password`}
                    value={passwords[field]}
                    onChange={(e) =>
                      setPasswords({ ...passwords, [field]: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-3 pr-10 focus:outline-none"
                    disabled={isPasswordLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-4 text-gray-500"
                    onClick={() => setShow({ ...show, [field]: !show[field] })}
                    tabIndex={-1}
                    disabled={isPasswordLoading}
                  >
                    {show[field] ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
            ))}

            <button
              type="submit"
              onClick={handleChangePassword}
              className={`w-full mt-4 flex items-center gap-2 justify-center bg-lightBlue text-white px-5 py-3 active:bg-darkBlue rounded transition-colors ${
                isPasswordLoading ? "opacity-60 cursor-not-allowed" : ""
              }`}
              disabled={isPasswordLoading}
            >
              {isPasswordLoading ? (
                profileButtonSpinner
              ) : (
                <KeyRound size={20} />
              )}
              {isPasswordLoading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>
      )}

      {/* Delete organisation confirmation */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="flex items-center gap-3 px-6 py-4 border-b">
              <AlertTriangle className="text-red-600" size={22} />
              <h3 className="text-lg font-semibold">Delete organisation?</h3>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                This permanently deletes <strong>{companyName}</strong> and
                everything in it:
              </p>
              <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                <li>All users, including your own account</li>
                <li>All projects, workflows and their saved versions</li>
                <li>Project access requests and Ledgerate AI chats</li>
              </ul>
              <p className="text-sm font-medium text-red-700">
                This cannot be undone.
              </p>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Type <strong>{companyName}</strong> to confirm
                </label>
                <input
                  type="text"
                  value={deleteForm.companyName}
                  onChange={(e) =>
                    setDeleteForm({ ...deleteForm, companyName: e.target.value })
                  }
                  disabled={isDeleting}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your password
                </label>
                <input
                  type="password"
                  value={deleteForm.password}
                  onChange={(e) =>
                    setDeleteForm({ ...deleteForm, password: e.target.value })
                  }
                  disabled={isDeleting}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={closeDeleteModal}
                disabled={isDeleting}
                className="px-5 py-2 border border-gray-300 rounded bg-white hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCompany}
                disabled={
                  isDeleting ||
                  !deleteForm.password ||
                  deleteForm.companyName.trim() !== companyName
                }
                className="flex items-center gap-2 px-5 py-2 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? profileButtonSpinner : <Trash2 size={18} />}
                {isDeleting ? "Deleting..." : "Delete Organisation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
